import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { fetchPaylocityPages } from './paylocityRequests';
import { selectApiUrls } from '../redux/slices/apiUrlSlice';
import { useGetJobListQuery, useGetSoldToShipToQuery } from '../redux/api/portalApi';
import { buildTimeComparison, toPaylocityJob } from './timeComparison';
import EngineeringTimeOverview from './EngineeringTimeOverview';
import { fetchEngineeringSchedule, mergeEngineeringSchedule } from './engineeringScheduleRequests';
import { projectValue } from './engineeringTimeOverviewUtils';

const hours = value => value == null ? '—' : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function AdminTimeComparison() {
  const { data: projects = [], isFetching, error: projectError, refetch } = useGetJobListQuery(undefined, { refetchOnMountOrArgChange: true });
  const { currentData: soldToShipTo = [], isFetching: marginLoading, error: marginError, refetch: refetchMargins } = useGetSoldToShipToQuery(undefined, { refetchOnMountOrArgChange: true });
  const apiUrls = useSelector(selectApiUrls);
  const [jobNumber, setJobNumber] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const request = useRef(null);
  useEffect(() => () => request.current?.abort(), []);
  const [scheduleRevision, setScheduleRevision] = useState(0);
  const [schedule, setSchedule] = useState({ projects: [], loading: true, error: '' });
  useEffect(() => {
    const controller = new AbortController();
    setSchedule({ projects: [], loading: true, error: '' });
    if (!apiUrls.fullSchedule) {
      setSchedule({ projects: [], loading: false, error: 'The full-schedule API URL is not configured.' });
      return () => controller.abort();
    }
    fetchEngineeringSchedule(apiUrls.fullSchedule, controller.signal).then(data => {
      if (!controller.signal.aborted) setSchedule({ projects: data, loading: false, error: '' });
    }).catch(err => {
      if (!controller.signal.aborted) setSchedule({ projects: [], loading: false, error: err.message || 'Unable to load engineering schedule.' });
    });
    return () => controller.abort();
  }, [apiUrls.fullSchedule, scheduleRevision]);
  const overviewProjects = useMemo(() => {
    const margins = new Map();
    if (!marginError && Array.isArray(soldToShipTo)) {
      soldToShipTo.forEach(record => {
        const id = projectValue(record, 'ProjectID') ?? projectValue(record, 'ID');
        if (id != null) margins.set(String(id).trim(), projectValue(record, 'GrossMarginPercent'));
      });
    }
    return mergeEngineeringSchedule(projects, schedule.projects).map(project => ({
      ...project,
      GrossMarginPercent: margins.get(String(projectValue(project, 'ID')).trim()),
    }));
  }, [projects, schedule.projects, soldToShipTo, marginError]);
  const refreshOverview = () => { refetch(); refetchMargins(); setScheduleRevision(value => value + 1); };

  const totals = result?.rows.reduce((summary, row) => {
    if (row.allocated != null) summary.allocated = (summary.allocated ?? 0) + row.allocated;
    if (row.variance != null) summary.variance = (summary.variance ?? 0) + row.variance;
    if (row.hasTime) {
      ['regular', 'ot1', 'ot2', 'actual', 'unpaid'].forEach(key => {
        summary[key] = (summary[key] ?? 0) + row[key];
      });
    }
    return summary;
  }, {});

  const compare = async (event, selectedNumber) => {
    event.preventDefault();
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setResult(null);
    setError('');
    setLoading(true);
    try {
      const number = (selectedNumber ?? jobNumber).trim();
      if (selectedNumber != null) setJobNumber(number);
      const paylocityJob = toPaylocityJob(number);
      const matches = projects.filter(project => String(project.JobNumber ?? project.jobNumber ?? '').trim() === number);
      if (matches.length !== 1) throw new Error(matches.length ? 'Multiple projects have this job number. Resolve the duplicate before comparing time.' : 'No project found for this job number.');
      const records = await fetchPaylocityPages(apiUrls.paylocity, paylocityJob, controller.signal);
      const rows = buildTimeComparison(matches[0], records, paylocityJob).filter(row => ['MECHANICAL ENGINEERING', 'ELECTRICAL ENGINEERING'].includes(row.department));
      if (!controller.signal.aborted) setResult({ number, paylocityJob, project: matches[0], rows });
    } catch (err) {
      if (!controller.signal.aborted) setError(err.message || 'Unable to load tracked time. Please try again.');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  return <Card className="mb-4">
    <Card.Body>
      <h3>Allocated vs Actual Time</h3>
      <p className="text-muted">Engineering allocations use 8 hours per day. Actual hours include regular time + OT1 + OT2; unpaid time is shown separately. Overtime hours are not multiplied.</p>
      {!apiUrls.paylocity && <Alert variant="warning">The Paylocity API URL is not configured.</Alert>}
      {projectError && <Alert variant="danger">Unable to load project allocations. <Button variant="link" onClick={refetch}>Retry</Button></Alert>}
      {schedule.error && <Alert variant="danger">{schedule.error} <Button variant="link" onClick={refreshOverview}>Retry schedule</Button></Alert>}
      {marginLoading && <p role="status">Loading gross margins…</p>}
      {marginError && <Alert variant="danger">Unable to load gross margins. <Button variant="link" onClick={refetchMargins}>Retry</Button></Alert>}
      <EngineeringTimeOverview projects={overviewProjects} projectLoading={isFetching || schedule.loading} projectError={projectError || schedule.error} endpoint={apiUrls.paylocity} onRefreshProjects={refreshOverview} onSelectJob={compare}>
      <h4>Job department breakdown</h4>
      <Form onSubmit={compare} className="mb-4">
        <Form.Group controlId="time-comparison-job" className="mb-2">
          <Form.Label>Job number</Form.Label>
          <Form.Control value={jobNumber} onChange={event => setJobNumber(event.target.value)} placeholder="Enter seven-digit job number" inputMode="numeric" pattern="[0-9]{7}" required list="time-comparison-jobs" style={{ maxWidth: 380 }} />
          <datalist id="time-comparison-jobs">{projects.map((project, index) => <option key={project.ID ?? project.id ?? index} value={project.JobNumber ?? project.jobNumber}>{project.ProjectName ?? project.projectName}</option>)}</datalist>
        </Form.Group>
        <Button type="submit" disabled={loading || isFetching || !!projectError || !apiUrls.paylocity}>{loading ? 'Loading tracked time…' : isFetching ? 'Loading projects…' : 'Compare time'}</Button>
      </Form>
      {error && <Alert variant="danger">{error}</Alert>}
      {result && <>
        <h4>Job {result.number} <small className="text-muted">({result.paylocityJob})</small></h4>
        <p>{result.project.ProjectName ?? result.project.projectName}</p>
        {!result.rows.some(row => row.hasTime) && <Alert variant="info">No tracked time returned for this job.</Alert>}
        <Table responsive striped bordered>
          <caption>All values are hours. Positive variance means over allocation. Missing time is not treated as zero usage.</caption>
          <thead><tr><th>Department</th><th>Allocated</th><th>Regular</th><th>OT1</th><th>OT2</th><th>Actual</th><th>Unpaid</th><th>Actual − allocated</th></tr></thead>
          <tbody>{result.rows.map(row => <tr key={row.department}>
            <th scope="row">{row.department}{!row.hasTime && <small className="d-block text-muted">No tracked time</small>}</th>
            <td>{hours(row.allocated)}</td>
            {['regular', 'ot1', 'ot2', 'actual', 'unpaid'].map(key => <td key={key}>{hours(row.hasTime ? row[key] : null)}</td>)}
            <td className={row.variance > 0 ? 'text-danger fw-bold' : ''}>{row.variance > 0 ? '+' : ''}{hours(row.variance)}</td>
          </tr>)}</tbody>
          <tfoot className="table-light fw-bold">
            <tr>
              <th scope="row">Totals</th>
              {['allocated', 'regular', 'ot1', 'ot2', 'actual', 'unpaid'].map(key => <td key={key}>{hours(totals[key])}</td>)}
              <td className={totals.variance > 0 ? 'text-danger' : ''}>{totals.variance > 0 ? '+' : ''}{hours(totals.variance)}</td>
            </tr>
          </tfoot>
        </Table>
        <p className="text-muted">Totals cover the departments in this table. Allocated hours sum known allocations; variance totals include only departments with both an allocation and tracked time.</p>
      </>}
      </EngineeringTimeOverview>
    </Card.Body>
  </Card>;
}
